/**
 * Fleet Zone music-channel assignment (Remote Console → KrossKast App).
 * Uses existing PUT/GET /v1/sa/device-fleet/zones/{zoneId}/channel.
 * Never invents a second PubNub command.
 */

export type ZoneAuthorizedChannel = {
  channelId: string;
  name?: string | null;
  status?: string | null;
};

export type ZoneChannelAssignmentSnapshot = {
  zoneId: string;
  locationId?: string | null;
  channelId?: string | null;
  channelName?: string | null;
  channelStatus?: string | null;
  assignmentRevision: number;
  assignmentSaved?: boolean;
  notifyStatus?: string | null;
  deviceConfirmed?: boolean;
  confirmedDeviceIds?: string[];
  pendingAckDeviceIds?: string[];
  occupantDeviceIds?: string[];
  unchanged?: boolean;
  notifiedDeviceIds?: string[];
  notifyFailures?: Array<{ deviceId?: string; code?: string; message?: string }>;
  authorizedChannels?: ZoneAuthorizedChannel[];
  brandId?: string | null;
  masterAccountId?: string | null;
};

export function buildZoneChannelPath(
  zoneId: string,
  opts?: { includeAuthorized?: boolean }
): string {
  const base = `v1/sa/device-fleet/zones/${encodeURIComponent(zoneId)}/channel`;
  if (opts?.includeAuthorized) {
    return `${base}?includeAuthorized=true`;
  }
  return base;
}

export function buildAssignZoneChannelRequest(input: {
  zoneId: string;
  channelId: string;
  expectedRevision?: number | null;
  retryNotify?: boolean;
}): { url: string; method: "PUT"; body: Record<string, unknown> } {
  const body: Record<string, unknown> = {
    channelId: input.channelId,
  };
  if (input.expectedRevision != null && Number.isFinite(input.expectedRevision)) {
    body.expectedRevision = input.expectedRevision;
  }
  if (input.retryNotify === true) {
    body.retryNotify = true;
  }
  return {
    url: buildZoneChannelPath(input.zoneId),
    method: "PUT",
    body,
  };
}

/** Confirmed for this device only when Fleet lists it in confirmedDeviceIds. */
export function isDeviceConfirmedForAssignment(
  snapshot: ZoneChannelAssignmentSnapshot | null | undefined,
  deviceId: string | null | undefined
): boolean {
  if (!snapshot || !deviceId) return false;
  const confirmed = snapshot.confirmedDeviceIds || [];
  if (confirmed.includes(deviceId)) return true;
  // Empty-zone / no occupants: never treat zone-wide deviceConfirmed alone
  // as this device's confirmation when we have a deviceId.
  if ((snapshot.occupantDeviceIds || []).includes(deviceId)) {
    return false;
  }
  return false;
}

export function confirmedChannelLabel(
  snapshot: ZoneChannelAssignmentSnapshot | null | undefined,
  deviceId: string | null | undefined
): string {
  if (!snapshot) return "Unknown";
  if (!isDeviceConfirmedForAssignment(snapshot, deviceId)) {
    return "Not confirmed";
  }
  const name = (snapshot.channelName || "").trim();
  const id = (snapshot.channelId || "").trim();
  if (name && id) return `${name} (${id})`;
  if (name) return name;
  if (id) return id;
  return "Not confirmed";
}

export function requestedVsConfirmedCopy(args: {
  requestedChannelId: string | null;
  requestedChannelName?: string | null;
  snapshot: ZoneChannelAssignmentSnapshot | null;
  deviceId: string | null;
}): string | null {
  const requested = (args.requestedChannelId || "").trim();
  if (!requested) return null;
  const confirmed =
    isDeviceConfirmedForAssignment(args.snapshot, args.deviceId) &&
    (args.snapshot?.channelId || "").trim() === requested;
  if (confirmed) return null;
  const reqLabel =
    (args.requestedChannelName || "").trim() || requested;
  const confLabel = confirmedChannelLabel(args.snapshot, args.deviceId);
  return `Requested: ${reqLabel} · Confirmed: ${confLabel}`;
}
